import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Helper function to convert image URL to base64
// Uses a proxy approach to avoid CORS tainted canvas issues
const getImageBase64 = async (url) => {
  try {
    // Fetch the image as a blob (this respects CORS but doesn't taint canvas)
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    
    // Create an object URL from the blob
    const objectUrl = URL.createObjectURL(blob);
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/jpeg', 0.7);
          
          // Clean up the object URL
          URL.revokeObjectURL(objectUrl);
          
          resolve(dataURL);
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Failed to convert image to canvas: ' + error.message));
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };
      
      // Load from the object URL (this won't taint the canvas)
      img.src = objectUrl;
    });
  } catch (error) {
    console.error('Error fetching image:', error);
    throw error;
  }
};

// Helper function to clean emojis and special characters
const cleanText = (text) => {
  if (!text) return '';
  
  // Map common emojis to text equivalents
  const emojiMap = {
    '1️⃣': '1.',
    '2️⃣': '2.',
    '3️⃣': '3.',
    '4️⃣': '4.',
    '5️⃣': '5.',
    '6️⃣': '6.',
    '7️⃣': '7.',
    '8️⃣': '8.',
    '9️⃣': '9.',
    '🔟': '10.',
    '👉': '=>',
    '✅': '[OK]',
    '❌': '[X]',
    '⚠️': '[!]',
    '📌': '*',
    '📎': '[Adjunto]',
    '💡': '[Idea]',
    '🔧': '[Herramienta]',
    '🚀': '[Inicio]',
    '✨': '*',
    '🎯': '[Objetivo]',
    '📊': '[Grafico]',
    '📈': '[Crecimiento]',
    '📉': '[Decrecimiento]',
    '🔍': '[Buscar]',
    '💬': '[Comentario]',
    '📝': '[Nota]',
    '🗓️': '[Calendario]',
    '⏰': '[Hora]',
    '🔔': '[Notificacion]',
    '⭐': '*',
    '🎉': '[Celebracion]',
    '🔥': '[Importante]',
    '💪': '[Fuerza]',
    '👍': '[OK]',
    '👎': '[No]',
    '🤔': '[Pensar]',
    '😊': ':)',
    '😃': ':)',
    '😄': ':)',
    '😁': ':)',
    '😅': ':)',
    '😂': ':D',
    '🤣': ':D',
    '😉': ';)',
    '😎': 'B)',
    '😍': '<3',
    '🥰': '<3',
    '😘': ':*',
    '😢': ':(',
    '😭': ':(',
    '😡': '>:(',
    '😱': ':O',
    '🙏': '[Gracias]',
  };
  
  // Replace known emojis first
  let cleanedText = text;
  Object.keys(emojiMap).forEach(emoji => {
    cleanedText = cleanedText.split(emoji).join(emojiMap[emoji]);
  });
  
  // Remove any remaining emojis and special Unicode characters
  // Keep only basic Latin characters, numbers, common punctuation, and Spanish characters
  cleanedText = cleanedText.replace(/[^\u0020-\u007E\u00A0-\u00FF\u0100-\u017F\n\r\t]/g, '');
  
  return cleanedText;
};

export const exportTicketToPDF = async (solicitud, imageUrls = {}, commentImageUrls = {}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let yPosition = 20;

  // Helper function to add text with word wrap
  const addWrappedText = (text, x, y, maxWidth, lineHeight = 7) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + (lines.length * lineHeight);
  };

  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  // Consecutivo (if exists)
  if (solicitud.consecutivo) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text(`Ticket #${solicitud.consecutivo}`, margin, yPosition);
    yPosition += 8;
    doc.setTextColor(0, 0, 0);
  }

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  yPosition = addWrappedText(cleanText(solicitud.titulo), margin, yPosition, pageWidth - 2 * margin, 10);
  yPosition += 5;

  // Metadata
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Creado por: ${cleanText(solicitud.creadoPor?.nombre || 'N/A')}`, margin, yPosition);
  yPosition += 6;
  doc.text(`Fecha: ${new Date(solicitud.fechaCreacion).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}`, margin, yPosition);
  yPosition += 10;

  // Badges section
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  
  const badges = [
    { label: 'Tipo', value: cleanText(solicitud.tipo) },
    { label: 'Prioridad', value: cleanText(solicitud.prioridad) },
    { label: 'Estado', value: cleanText(solicitud.estado.replace('_', ' ')) },
  ];
  
  if (solicitud.modulo) {
    badges.push({ label: 'Módulo', value: cleanText(solicitud.modulo) });
  }
  
  if (solicitud.asignadoA) {
    badges.push({ label: 'Asignado a', value: cleanText(solicitud.asignadoA.nombre) });
  }

  badges.forEach((badge, index) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${badge.label}:`, margin + (index * 40), yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(badge.value, margin + (index * 40), yPosition + 5);
  });
  
  yPosition += 15;

  // Separator line
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Description section
  checkNewPage(30);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Descripción', margin, yPosition);
  yPosition += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  yPosition = addWrappedText(cleanText(solicitud.descripcion || 'Sin descripción'), margin, yPosition, pageWidth - 2 * margin);
  yPosition += 10;

  // Attachments section
  if (solicitud.adjuntos && solicitud.adjuntos.length > 0) {
    checkNewPage(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Imágenes adjuntas (${solicitud.adjuntos.length})`, margin, yPosition);
    yPosition += 8;

    // Try to load and embed images
    for (let i = 0; i < solicitud.adjuntos.length; i++) {
      const adjuntoId = solicitud.adjuntos[i];
      const imageUrl = imageUrls[adjuntoId];
      
      
      if (imageUrl) {
        try {
          checkNewPage(80);
          const base64Image = await getImageBase64(imageUrl);
          
          // Calculate image dimensions to fit in PDF
          const maxWidth = pageWidth - 2 * margin;
          const maxHeight = 70;
          
          doc.addImage(base64Image, 'JPEG', margin, yPosition, maxWidth, maxHeight);
          yPosition += maxHeight + 5;
          
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(`Imagen ${i + 1}`, margin, yPosition);
          yPosition += 8;
          doc.setTextColor(0, 0, 0);
        } catch (error) {
          console.error(`Error loading image ${i + 1}:`, error);
          console.error('Image URL:', imageUrl);
          console.error('Error details:', error.message, error.stack);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 100, 100);
          doc.text(`• Imagen ${i + 1} (no se pudo cargar: ${error.message})`, margin + 5, yPosition);
          yPosition += 6;
          doc.setTextColor(0, 0, 0);
        }
      } else {
        console.warn(`No URL available for attachment ${i + 1}, ID:`, adjuntoId);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`• Imagen ${i + 1} (URL no disponible)`, margin + 5, yPosition);
        yPosition += 6;
        doc.setTextColor(0, 0, 0);
      }
    }
    yPosition += 5;
  }

  // Comments section
  if (solicitud.comentarios && solicitud.comentarios.length > 0) {
    checkNewPage(30);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Comentarios (${solicitud.comentarios.length})`, margin, yPosition);
    yPosition += 10;

    for (let index = 0; index < solicitud.comentarios.length; index++) {
      const comentario = solicitud.comentarios[index];
      checkNewPage(40);
      
      // Comment header
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(cleanText(comentario.usuario.nombre), margin + 5, yPosition);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const fechaComentario = new Date(comentario.fecha).toLocaleString('es-ES');
      doc.text(fechaComentario, pageWidth - margin - doc.getTextWidth(fechaComentario), yPosition);
      yPosition += 7;

      // Comment content
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      yPosition = addWrappedText(cleanText(comentario.contenido), margin + 5, yPosition, pageWidth - 2 * margin - 5, 6);
      yPosition += 5;
      
      // Comment attachments - Show actual images
      if (comentario.adjuntos && comentario.adjuntos.length > 0) {
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(`Imagenes adjuntas (${comentario.adjuntos.length}):`, margin + 5, yPosition);
        yPosition += 5;
        
        for (let i = 0; i < comentario.adjuntos.length; i++) {
          const adjuntoId = comentario.adjuntos[i];
          const imageUrl = commentImageUrls[adjuntoId];
          
          if (imageUrl) {
            try {
              checkNewPage(60);
              const base64Image = await getImageBase64(imageUrl);
              
              // Calculate image dimensions for comment images (smaller)
              const maxWidth = (pageWidth - 2 * margin - 10) / 2;
              const maxHeight = 50;
              
              doc.addImage(base64Image, 'JPEG', margin + 10, yPosition, maxWidth, maxHeight);
              yPosition += maxHeight + 3;
            } catch (error) {
              console.error('Error loading comment image:', error);
              doc.text(`  • Imagen ${i + 1} (no se pudo cargar)`, margin + 10, yPosition);
              yPosition += 5;
            }
          } else {
            doc.text(`  • Imagen ${i + 1} (no disponible)`, margin + 10, yPosition);
            yPosition += 5;
          }
        }
        yPosition += 3;
      }
      
      yPosition += 8;
      doc.setTextColor(0, 0, 0);

      // Separator between comments
      if (index < solicitud.comentarios.length - 1) {
        doc.setDrawColor(230, 230, 230);
        doc.line(margin + 5, yPosition, pageWidth - margin - 5, yPosition);
        yPosition += 8;
      }
    }
  }

  // History section
/*   if (solicitud.historial && solicitud.historial.length > 0) {
    checkNewPage(30);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Historial de Cambios', margin, yPosition);
    yPosition += 10;

    solicitud.historial.forEach((cambio) => {
      checkNewPage(15);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      const historialText = `${cleanText(cambio.usuario.nombre)} cambió ${cleanText(cambio.campo)} de "${cleanText(cambio.valorAnterior)}" a "${cleanText(cambio.valorNuevo)}"`;
      yPosition = addWrappedText(historialText, margin + 5, yPosition, pageWidth - 2 * margin - 5, 6);
      
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const fechaCambio = new Date(cambio.fecha).toLocaleString('es-ES');
      doc.text(fechaCambio, margin + 5, yPosition);
      yPosition += 8;
      doc.setTextColor(0, 0, 0);
    });
  } */

  // Footer on each page
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Página ${i} de ${totalPages} - Generado el ${new Date().toLocaleDateString('es-ES')}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  // Generate filename
  const filename = `Ticket_${solicitud._id || 'sin-id'}_${solicitud.titulo.substring(0, 30).replace(/[^a-z0-9]/gi, '_')}.pdf`;
  
  // Save the PDF
  doc.save(filename);
};
